/* eslint-disable react-hooks/exhaustive-deps */
import {
  AlertColor,
  Avatar,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import ConfirmModal from 'components/ConfirmModal';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { filterDataforApi, pageNo, rowCount, setPageNo } from 'utils/redux/reducer/filter-slice';
import strings from '../../../common/Translation/Translate';
import Notification from '../../../components/Notification';
import PageLoader from '../../../components/PageLoader';
import MultiSelect from '../../../components/MultiSelect/MultiSelect';
import axiosInstance from '../../../utils/axios';
import { getTableAvatarSx } from '../../../utils/avatarColors';
import { PageProps } from '../../../utils/constants/data';
import { privileges } from '../../../utils/constants/privileges';
import { routes } from '../../../utils/constants/routes';
import { URLS } from '../../../utils/constants/urls';
import { Privilege } from '../../../utils/redux/reducer/authentication-slice';
import { createDropdownData } from '../../../utils/validators/HelperFunctions';
import DateRangeSelector from '../BookingEventSource/DateRangeSelector';
import ListPage from '../CommonPage';
import BookingStatusCards from './BookingStatusCards';
import './BookingList.scss';

type ListType = 'active' | 'deleted';

const getCustomerInitials = (name?: string) => {
  if (!name?.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const STATUS_FILTER_OPTIONS = [
  { value: 'TENTATIVE', label: strings.tentativeText },
  { value: 'WAITLISTED', label: strings.waitListText },
  { value: 'CONFIRMED', label: strings.confirmedText },
  { value: 'ENQUIRY', label: 'ENQUIRY' },
  { value: 'AWAITING_CONFIRMATION', label: strings.awaitingConfirmationText },
  { value: 'ACTUALIZED', label: strings.actualization_text },
  { value: 'CANCELLED', label: strings.cancelledText },
  { value: 'EXPIRED', label: strings.expired_text },
  { value: 'HIGH_DEMAND', label: strings.highDemand_text },
  { value: 'UNAVAILABLE', label: strings.unavailable_text },
  { value: 'BLOCKED', label: strings.blocked_text },
];

const stripFilterKey = (filter: unknown, key: string) => {
  const base = Array.isArray(filter)
    ? filter.filter((item: unknown) => typeof item !== 'string' || !item.startsWith(`${key}:`))
    : [];

  const cleaned: string[] = [];
  base.forEach((item: string) => {
    if (item === 'and') {
      if (cleaned.length === 0 || cleaned[cleaned.length - 1] === 'and') return;
      cleaned.push(item);
      return;
    }
    cleaned.push(item);
  });
  if (cleaned.length && cleaned[cleaned.length - 1] === 'and') cleaned.pop();
  return cleaned;
};

const withStatusFilter = (filter: unknown, status: string) => {
  const cleaned = stripFilterKey(filter, 'status');
  if (!status) return cleaned;
  if (cleaned.length) cleaned.push('and');
  cleaned.push(`status:cic:${status}`);
  return cleaned;
};

const withDateRangeFilter = (filter: unknown, startDate: Dayjs, endDate: Dayjs) => {
  let next = stripFilterKey(filter, 'eventStartDate');
  next = stripFilterKey(next, 'eventEndDate');
  if (next.length) next.push('and');
  next.push(`eventStartDate:cic:${startDate.format('YYYY-MM-DD')}`);
  next.push('and');
  next.push(`eventEndDate:cic:${endDate.format('YYYY-MM-DD')}`);
  return next;
};

const withBookingFilters = (filter: unknown, status: string, startDate: Dayjs, endDate: Dayjs) =>
  withDateRangeFilter(withStatusFilter(filter, status), startDate, endDate);

const dateRangeFilterSx = {
  flex: '1.25 1 0 !important',
  minWidth: 0,
  bgcolor: '#fff',
  '& .MuiInputBase-root': {
    fontSize: 13,
    minHeight: 36,
    height: 36,
  },
  '& .MuiInputLabel-root': {
    fontSize: 12,
  },
};

const formatStatusLabel = (status?: string | null) => {
  if (!status) return '—';
  const match = STATUS_FILTER_OPTIONS.find(option => option.value === status);
  return match?.label || String(status).replace(/_/g, ' ');
};

const listTypeToggleSx = {
  flexShrink: 0,
  height: 36,
  '& .MuiToggleButton-root': {
    px: 1.5,
    py: 0,
    height: 36,
    fontSize: 13,
    textTransform: 'none',
    fontWeight: 500,
  },
  '& .MuiToggleButton-root.Mui-selected': {
    fontWeight: 700,
  },
};

const BookingList: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const userPrivileges = useSelector(Privilege);
  const theme = useTheme();

  const canAssign = userPrivileges?.includes(privileges.assign_booking);
  const canViewDeleted = userPrivileges?.includes(privileges.deleteBooking);

  const initialListType: ListType =
    canViewDeleted && searchParams.get('view') === 'deleted' ? 'deleted' : 'active';

  const [listType, setListType] = useState<ListType>(initialListType);
  const [assigneeOptions, setAssigneeOptions] = useState<any[]>([]);
  const [pageResponse, setPageResponse] = useState<any>({});
  const [bookingStats, setBookingStats] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs().endOf('month'));
  const [confirmModal, setConfirmModal] = useState(false);
  const [formDefaultData, setFormDefaultData] = useState<any>();
  const [message, setMessage] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const isDeletedView = listType === 'deleted';

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const resetToFirstPage = useCallback(() => {
    dispatch(setPageNo(0));
  }, [dispatch]);

  const resetDateRangeToCurrentMonth = useCallback(() => {
    setStartDate(dayjs().startOf('month'));
    setEndDate(dayjs().endOf('month'));
  }, []);

  const handleStatusFilterChange = (value: string) => {
    setSelectedStatus(value);
    resetToFirstPage();
  };

  const handleDateRangeChange = (nextStart: Dayjs, nextEnd: Dayjs) => {
    setStartDate(nextStart);
    setEndDate(nextEnd);
    resetToFirstPage();
  };

  const handleListTypeChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, value: ListType | null) => {
      if (!value || value === listType) return;
      setListType(value);
      setSelectedStatus('');
      resetDateRangeToCurrentMonth();
      setPageResponse({});
      resetToFirstPage();
      if (value === 'deleted') {
        setSearchParams({ view: 'deleted' }, { replace: true });
      } else {
        setSearchParams({}, { replace: true });
      }
    },
    [listType, resetDateRangeToCurrentMonth, resetToFirstPage, setSearchParams],
  );

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error.response?.data?.error_description ?? error.response?.data?.message ?? error.message,
    });
  };

  const getChipStyles = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'CONFIRMED':
        return { backgroundColor: '#E6F4EA', color: '#188038' };
      case 'TENTATIVE':
        return { backgroundColor: '#FFF4E5', color: '#D17A00' };
      case 'WAITLISTED':
        return { backgroundColor: '#E8F0FE', color: '#1967D2' };
      case 'CANCELLED':
        return { backgroundColor: '#FDECEA', color: '#D93025' };
      default:
        return { backgroundColor: theme.palette.secondary.light, color: theme.palette.primary.main };
    }
  };

  const assignBookings = async (value: any, id: any) => {
    try {
      setIsLoading(true);
      await axiosInstance.post(URLS.assignBookings, {
        lineItemId: id,
        assigneeId: value.value,
      });
    } catch (err) {
      handleCatch(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = async () => {
    setConfirmModal(false);
    await assignBookings(formDefaultData?.value, formDefaultData.id);
    await fetchBookings(null as any);
  };

  const loadBookingStats = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(URLS.getBookingStats, {
        params: {
          fromDate: startDate.format('YYYY-MM-DD'),
          toDate: endDate.format('YYYY-MM-DD'),
        },
      });
      setBookingStats(data);
    } catch (error: any) {
      handleCatch(error);
    }
  }, [startDate, endDate]);

  const loadAssigneeOptions = useCallback(async () => {
    if (!canAssign) return;
    try {
      const { data: salesData } = await axiosInstance.get(URLS.salesExecutivesDropdown);
      setAssigneeOptions(createDropdownData(salesData, ['id', 'name']) || []);
    } catch (err) {
      console.log(err);
    }
  }, [canAssign]);

  const fetchBookings = useCallback(
    async (filterData: PageProps) => {
      try {
        setIsLoading(true);

        const body = filterData
          ? {
              ...filterData,
              filter: withBookingFilters(filterData.filter, selectedStatus, startDate, endDate),
            }
          : {
              page: index + 1,
              size: pageSize,
              filter: withBookingFilters(filter, selectedStatus, startDate, endDate),
              sort: ['createdAt:desc'],
            };

        const listUrl = isDeletedView ? URLS.deletedBookings : URLS.bookingFilterList;
        const { data } = await axiosInstance.post(listUrl, body);
        setPageResponse(data);
      } catch (err) {
        handleCatch(err);
      } finally {
        setIsLoading(false);
      }
    },
    [index, pageSize, filter, selectedStatus, startDate, endDate, isDeletedView],
  );

  useEffect(() => {
    if (!isDeletedView) {
      void loadBookingStats();
    }
  }, [loadBookingStats, isDeletedView]);

  useEffect(() => {
    if (!isDeletedView) {
      void loadAssigneeOptions();
    }
  }, [loadAssigneeOptions, isDeletedView]);

  useEffect(() => {
    void fetchBookings(null as any);
  }, [fetchBookings]);

  const activeColumns = useMemo(
    () => [
      {
        field: 'customerName',
        headerName: 'Customer',
        flex: 1.8,
        sortable: false,
        renderCell: (_value: any, row: any) => {
          const name = row.customerName || '—';
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.25 }}>
              <Avatar sx={getTableAvatarSx(row.customerName)}>{getCustomerInitials(row.customerName)}</Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, lineHeight: 1.35, color: '#101828' }}>
                  {name}
                </Typography>
                {row.companyName ? (
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.35 }}>
                    {row.companyName}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          );
        },
      },
      {
        field: 'startDate',
        headerName: 'Event',
        flex: 1.5,
        sortable: true,
        renderCell: (_value: any, row: any) => (
          <>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.35, color: '#101828' }}>
              {row.startDate ? dayjs(row.startDate).format('D MMM YYYY') : '—'}
              {row.startTime ? ` · ${row.startTime}` : ''}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.35 }}>
              {row.eventTypeName || row.eventType || '—'}
            </Typography>
          </>
        ),
      },
      {
        field: 'venue',
        headerName: 'Venue',
        flex: 1.4,
        sortable: true,
        renderCell: (_value: any, row: any) => (
          <>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.35, color: '#101828' }}>
              {row.venue || '—'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.35 }}>
              {row.expectedGuests != null ? `${row.expectedGuests} guests` : '—'}
            </Typography>
          </>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1.1,
        sortable: true,
        renderCell: (value: any) => (
          <Chip
            label={formatStatusLabel(value)}
            size="small"
            sx={{ textTransform: 'capitalize', fontWeight: 600, ...getChipStyles(value) }}
          />
        ),
      },
      ...(canAssign
        ? [
              {
                field: 'assignee',
                headerName: 'Assigned to',
                flex: 1.8,
                sortable: false,
                textAlign: 'right',
                renderCell: (_value: any, row: any) => (
                  <Box
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      '& .selectAssignee': { width: '100%', maxWidth: 280 },
                    }}>
                    <MultiSelect
                      id={`assignee-${row.id}`}
                      placeholder={strings.bookingList_select_assignee}
                      field="org"
                      isClearable={false}
                      className="selectAssignee"
                      controlHeight={36}
                      options={assigneeOptions}
                      disabled={row.status === 'CANCELLED'}
                      onChange={async value => {
                        setConfirmModal(true);
                        setFormDefaultData({ value, id: row.id });
                      }}
                      inputValue={row.assignee}
                      style={{
                        singleValue: (base: any) => ({
                          ...base,
                          display: 'flex',
                          alignItems: 'center',
                          lineHeight: 1,
                          margin: 0,
                          top: 'auto',
                          transform: 'none',
                          position: 'static',
                        }),
                        placeholder: (base: any) => ({
                          ...base,
                          display: 'flex',
                          alignItems: 'center',
                          lineHeight: 1,
                          margin: 0,
                          top: 'auto',
                          transform: 'none',
                          position: 'static',
                        }),
                        input: (base: any) => ({
                          ...base,
                          margin: 0,
                          padding: 0,
                        }),
                        valueContainer: (base: any) => ({
                          ...base,
                          height: 36,
                          padding: '0 8px',
                          display: 'flex',
                          alignItems: 'center',
                        }),
                      }}
                    />
                  </Box>
                ),
              },
          ]
        : []),
    ],
    [canAssign, assigneeOptions, theme],
  );

  const deletedColumns = useMemo(
    () => [
      {
        field: 'customerName',
        headerName: 'Customer',
        flex: 1.6,
        sortable: false,
        renderCell: (_value: any, row: any) => {
          const name = row.customerName || '—';
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.25 }}>
              <Avatar sx={getTableAvatarSx(row.customerName)}>{getCustomerInitials(row.customerName)}</Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, lineHeight: 1.35, color: '#101828' }}>
                  {name}
                </Typography>
                {row.companyName ? (
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.35 }}>
                    {row.companyName}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          );
        },
      },
      {
        field: 'startDate',
        headerName: 'Event',
        flex: 1.4,
        sortable: true,
        renderCell: (_value: any, row: any) => (
          <>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.35, color: '#101828' }}>
              {row.startDate ? dayjs(row.startDate).format('D MMM YYYY') : '—'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.35 }}>
              {row.eventTypeName || row.eventType || '—'}
            </Typography>
          </>
        ),
      },
      {
        field: 'venue',
        headerName: 'Venue',
        flex: 1.2,
        sortable: true,
        renderCell: (value: any) => (
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: '#344054' }}>{value || '—'}</Typography>
        ),
      },
      {
        field: 'status',
        headerName: 'Status at Deletion',
        flex: 1.2,
        sortable: true,
        renderCell: (value: any) => (
          <Chip
            label={formatStatusLabel(value)}
            size="small"
            sx={{ textTransform: 'capitalize', fontWeight: 600, ...getChipStyles(value) }}
          />
        ),
      },
      {
        field: 'deletedOn',
        headerName: 'Deleted On',
        flex: 1.2,
        sortable: false,
        renderCell: (value: any) =>
          value ? (
            <>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.35 }}>
                {dayjs(value).format('D MMM YYYY')}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.35 }}>
                {dayjs(value).format('h:mm A')}
              </Typography>
            </>
          ) : (
            '—'
          ),
      },
      {
        field: 'deletedBy',
        headerName: 'Deleted By',
        flex: 1.1,
        sortable: false,
        renderCell: (value: any) => (
          <Typography sx={{ fontSize: '0.8125rem', color: '#344054' }}>{value || '—'}</Typography>
        ),
      },
      {
        field: 'deletionReason',
        headerName: 'Reason',
        flex: 1.6,
        sortable: false,
        renderCell: (value: any) => (
          <Typography
            sx={{
              fontSize: '0.8125rem',
              color: '#344054',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
            title={value || undefined}>
            {value || '—'}
          </Typography>
        ),
      },
    ],
    [theme],
  );

  const pageConfig = useMemo(
    () => ({
      title: isDeletedView ? strings.menu_text_deletedBookings : strings.menu_text_bookingList,
      entityLabel: isDeletedView ? 'Deleted Bookings' : 'Bookings',
      subtitle: isDeletedView
        ? 'Review bookings that were deleted, including who deleted them and why.'
        : 'Track bookings across venues, statuses, and assignees.',
      searchField: 'search',
      searchPlaceholder: 'Search by customer, event, or venue',
      listApi: isDeletedView ? URLS.deletedBookings : URLS.bookingFilterList,
      hideActionText: true,
      ...(isDeletedView
        ? {}
        : {
            importPrivilege: privileges.import_bookings,
            importIconOnly: true,
            importApi: URLS.import_booking,
            downloadImportTemplate: URLS.import_booking_template,
            onRowClick: (row: any) => {
              if (row?.id != null) navigate(`${routes.bookings}/${row.id}`);
            },
            renderKpis: () => (
              <BookingStatusCards
                totalBookings={bookingStats?.total || 0}
                confirmed={bookingStats?.confirmed || 0}
                awaitingConfirmation={bookingStats?.awaitingConfirmation || 0}
                tentative={bookingStats?.tentative || 0}
                waitlisted={bookingStats?.waitlisted || 0}
                enquiry={bookingStats?.enquiry || 0}
                rfq={bookingStats?.rfq || 0}
                conversionNumerator={bookingStats?.conversionNumerator || 0}
                conversionDenominator={bookingStats?.conversionDenominator || 0}
              />
            ),
          }),
      customComponent: canViewDeleted
        ? () => (
            <ToggleButtonGroup
              exclusive
              value={listType}
              onChange={handleListTypeChange}
              size="small"
              aria-label="Booking list type"
              sx={listTypeToggleSx}>
              <ToggleButton value="active">Active</ToggleButton>
              <ToggleButton value="deleted">Deleted</ToggleButton>
            </ToggleButtonGroup>
          )
        : undefined,
      headerFilters: () => (
        <>
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateRangeChange}
            sx={dateRangeFilterSx}
          />
          <FormControl size="small" sx={{ flex: '1 1 0', minWidth: 0 }}>
            <InputLabel sx={{ fontSize: 12 }}>Status</InputLabel>
            <Select
              label="Status"
              value={selectedStatus}
              onChange={event => handleStatusFilterChange(String(event.target.value))}
              sx={{ height: 36, fontSize: 13, bgcolor: '#fff', '& .MuiSelect-select': { py: 0.75 } }}>
              <MenuItem value="">All statuses</MenuItem>
              {STATUS_FILTER_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      ),
      filterChips: [
        {
          key: 'dateRange',
          label: `Date : ${startDate.format('D MMM YYYY')} – ${endDate.format('D MMM YYYY')}`,
        },
        ...(selectedStatus
          ? [{ key: 'status', label: `Status : ${formatStatusLabel(selectedStatus)}` }]
          : []),
      ],
      onClearFilterChip: (key: string) => {
        if (key === 'status') handleStatusFilterChange('');
        if (key === 'dateRange') {
          resetDateRangeToCurrentMonth();
          resetToFirstPage();
        }
      },
      onClearAllFilterChips: () => {
        handleStatusFilterChange('');
        resetDateRangeToCurrentMonth();
      },
      tableColumn: isDeletedView ? deletedColumns : activeColumns,
    }),
    [
      isDeletedView,
      bookingStats,
      selectedStatus,
      startDate,
      endDate,
      canViewDeleted,
      listType,
      handleListTypeChange,
      resetDateRangeToCurrentMonth,
      resetToFirstPage,
      activeColumns,
      deletedColumns,
      navigate,
    ],
  );

  return (
    <>
      {isLoading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <ListPage isLoading={isLoading} pageConfig={pageConfig} pageResponse={pageResponse} getList={fetchBookings} />
      {confirmModal && (
        <ConfirmModal
          message={strings.changeAssigneeConfirmation}
          open={confirmModal}
          handleYes={handleAction}
          closeModal={() => setConfirmModal(false)}
        />
      )}
    </>
  );
};

export default BookingList;
